
import React, { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ContactForm from '../../components/base/ContactForm';
import TestimonialsSlider from '../../components/base/TestimonialsSlider';

const GoodwoodPage: React.FC = () => {
  useEffect(() => {
    // Add Schema.org JSON-LD for Goodwood page
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Goodwood Painters",
      "description": "Professional painting contractors in Goodwood, Western Cape. Get quotes for interior, exterior, roof and commercial painting services.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/goodwood-painters`,
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Goodwood",
        "addressRegion": "Western Cape",
        "addressCountry": "ZA"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "-33.8847",
        "longitude": "18.5489"
      },
      "serviceArea": {
        "@type": "City",
        "name": "Goodwood"
      },
      "services": [
        "Residential Interior Painting",
        "Exterior House Painting",
        "Roof Painting",
        "Commercial Painting",
        "Affordable Painting Solutions",
        "Surface Preparation"
      ]
    });
    document.head.appendChild(script);

    // Update page title and meta
    document.title = "Goodwood Painters - Affordable Painting Contractors Western Cape | Free Quotes";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Affordable painting contractors in Goodwood, Western Cape. Get free quotes for interior, exterior, roof and commercial painting. Community-focused service, reliable contractors.');
    }

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative min-h-[70vh] flex items-center justify-center bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://public.readdy.ai/ai/img_res/5df986a3d8656553737c4a6f259d3444.jpg')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            <span className="block">Goodwood</span>
            <span className="block text-[#e9f638]">PAINTERS</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
            Get quotes from the best painting contractors in Goodwood
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Professional Painting Services in Goodwood
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Transform your Goodwood property with our network of skilled painting contractors. We connect you with professionals who understand the diverse architectural styles and strong community spirit that make Goodwood a unique and vibrant neighborhood in the Northern Suburbs of Cape Town.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Our Goodwood painting experts specialize in everything from cozy family homes to bustling commercial spaces. Each contractor is selected for their reliability, skill, and commitment to delivering outstanding results that enhance your property's value and appeal while respecting the community-focused nature of this established area.
              </p>
              <p className="text-lg text-gray-600 mb-8">
                Whether you're refreshing a starter home, updating a growing family's space, or maintaining commercial property in Goodwood's vibrant business areas, our contractors bring the expertise and professionalism you deserve, combined with an understanding of local needs and budget considerations.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] text-xl mr-3"></i>
                  <span className="text-gray-700">Diverse Property Types</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] text-xl mr-3"></i>
                  <span className="text-gray-700">Community-Focused Service</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] text-xl mr-3"></i>
                  <span className="text-gray-700">Affordable Solutions</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] text-xl mr-3"></i>
                  <span className="text-gray-700">Reliable Contractors</span>
                </div>
              </div>
            </div>
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20diverse%20residential%20homes%20in%20Goodwood%20with%20community%20atmosphere%20and%20varied%20architectural%20styles%2C%20quality%20painting%20equipment%2C%20clean%20professional%20appearance%2C%20bright%20natural%20lighting&width=600&height=400&seq=goodwood-painting-work&orientation=landscape"
                alt="Professional painters working in Goodwood"
                className="rounded-lg shadow-lg w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* About Goodwood Community */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center">
            Understanding Goodwood's Community Character and Painting Needs
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-lg text-gray-600 mb-6">
                Goodwood represents the heart of community living in Cape Town's Northern Suburbs, with a diverse mix of housing styles that reflect the area's evolution over the decades. From compact starter homes perfect for young families to larger properties that have grown with their owners, Goodwood's residential landscape requires painting contractors who understand both variety and value.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                The strong sense of community in Goodwood means that painting projects often become neighborhood talking points, with residents taking pride in maintaining and improving their properties. Our network of Goodwood painting professionals understands this community dynamic and works to ensure that every project enhances not just individual properties but contributes to the overall neighborhood appeal.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Goodwood's location and climate present specific challenges for painting contractors, from dealing with the area's varied building materials to understanding how local weather patterns affect paint selection and application timing. Our contractors bring this specialized local knowledge to every project, ensuring optimal results that stand the test of time.
              </p>
            </div>
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=Goodwood%20South%20Africa%20community%20neighborhood%20with%20diverse%20housing%20styles%2C%20family%20homes%2C%20local%20businesses%2C%20community%20atmosphere%2C%20residential%20streets%2C%20painting%20work%20in%20progress&width=600&height=400&seq=goodwood-community&orientation=landscape"
                alt="Goodwood community neighborhood"
                className="rounded-lg shadow-lg w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Goodwood Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h4 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Goodwood Painting Services
            </h4>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Affordable, quality painting solutions designed for Goodwood's diverse property types and community needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-lg border">
              <div className="w-12 h-12 bg-[#e9f638] rounded-lg flex items-center justify-center mb-4">
                <i className="ri-home-4-line text-2xl text-black"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-3">Goodwood Family Home Painting</h5>
              <p className="text-gray-600">Specialized painting services for Goodwood's diverse family homes, from compact starter properties to larger family residences, with solutions that fit every budget and lifestyle.</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg border">
              <div className="w-12 h-12 bg-[#e9f638] rounded-lg flex items-center justify-center mb-4">
                <i className="ri-building-line text-2xl text-black"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-3">Goodwood Exterior Painting</h5>
              <p className="text-gray-600">Professional exterior painting that enhances Goodwood's neighborhood appeal, using quality materials and techniques suited to the area's climate and architectural diversity.</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg border">
              <div className="w-12 h-12 bg-[#e9f638] rounded-lg flex items-center justify-center mb-4">
                <i className="ri-home-2-line text-2xl text-black"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-3">Goodwood Interior Transformation</h5>
              <p className="text-gray-600">Interior painting services that transform Goodwood homes into beautiful, comfortable living spaces, with color consultation and finish options for every taste and budget.</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg border">
              <div className="w-12 h-12 bg-[#e9f638] rounded-lg flex items-center justify-center mb-4">
                <i className="ri-office-building-line text-2xl text-black"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-3">Goodwood Commercial Painting</h5>
              <p className="text-gray-600">Professional painting for Goodwood's local businesses, retail spaces, and commercial properties, supporting the area's economic vitality with quality, affordable solutions.</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg border">
              <div className="w-12 h-12 bg-[#e9f638] rounded-lg flex items-center justify-center mb-4">
                <i className="ri-brush-line text-2xl text-black"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-3">Goodwood Affordable Solutions</h5>
              <p className="text-gray-600">Budget-conscious painting options that don't compromise on quality, designed specifically for Goodwood residents who want professional results at community-friendly prices.</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg border">
              <div className="w-12 h-12 bg-[#e9f638] rounded-lg flex items-center justify-center mb-4">
                <i className="ri-tools-line text-2xl text-black"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-3">Goodwood Maintenance Painting</h5>
              <p className="text-gray-600">Regular maintenance painting services that help Goodwood property owners protect their investments and maintain neighborhood standards without breaking the budget.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Goodwood Painters */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h4 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Our Goodwood Painting Network
            </h4>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Connecting you with Goodwood's most reliable and community-focused painting professionals
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#e9f638] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-map-pin-line text-2xl text-black"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-4">Goodwood Community Knowledge</h5>
              <p className="text-gray-600">Our contractors understand Goodwood's community character, property types, and local preferences, ensuring painting solutions that fit perfectly with neighborhood standards.</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-[#e9f638] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-award-line text-2xl text-black"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-4">Goodwood Quality Commitment</h5>
              <p className="text-gray-600">All our Goodwood painters are committed to delivering quality workmanship that enhances property values and contributes to the area's continued improvement and appeal.</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-[#e9f638] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-time-line text-2xl text-black"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-4">Goodwood Reliable Service</h5>
              <p className="text-gray-600">Dependable contractors who understand the importance of reliability in a close-knit community like Goodwood, completing projects on time and maintaining excellent relationships.</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-[#e9f638] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-money-dollar-circle-line text-2xl text-black"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-4">Goodwood Value Pricing</h5>
              <p className="text-gray-600">Competitive, transparent pricing that reflects Goodwood's community values, ensuring quality painting services remain accessible to all residents and business owners.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Goodwood Area Coverage */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h4 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
            Complete Goodwood Area Coverage
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <h6 className="text-xl font-semibold text-gray-900 mb-3">Goodwood Residential Areas</h6>
              <p className="text-gray-600">Comprehensive painting services for all Goodwood residential neighborhoods, from established family areas to newer developments and everything in between.</p>
            </div>
            <div className="text-center p-6">
              <h6 className="text-xl font-semibold text-gray-900 mb-3">Goodwood Commercial District</h6>
              <p className="text-gray-600">Professional painting for Goodwood's business areas, supporting local commerce with quality, affordable painting solutions for shops, offices, and service businesses.</p>
            </div>
            <div className="text-center p-6">
              <h6 className="text-xl font-semibold text-gray-900 mb-3">Greater Goodwood Area</h6>
              <p className="text-gray-600">Extended coverage throughout the broader Goodwood region, including surrounding neighborhoods and developing areas that benefit from our community-focused approach.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h4 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Get Your Free Goodwood Painting Quotes
            </h4>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Ready to transform your Goodwood property? Connect with trusted painting contractors who understand your neighborhood and offer competitive, community-friendly pricing
            </p>
          </div>
          <ContactForm />
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h4 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Goodwood Residents Say
            </h4>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Hear from satisfied Goodwood homeowners and business owners who found their perfect painting contractor through our trusted community network
            </p>
          </div>
          <TestimonialsSlider />
        </div>
      </section>

      <Footer />
      
      {/* Elfsight AI Chatbot */}
      <div>
        <script src="https://elfsightcdn.com/platform.js" async></script>
        <div className="elfsight-app-bd0c8f1c-533d-413e-bee7-63e0ccb9fdbf" data-elfsight-app-lazy></div>
      </div>
    </div>
  );
};

export default GoodwoodPage;
